/**
 * DTemplate Team Slide
 * The decks' own team slide on DTemplate's canvas — the founder, the AI
 * team — with what's been built so far and the team this round makes
 */

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { StatementSlide, useArrive } from '@components/press/pitch-deck/slides/dtemplate/frame';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

import linkedinLogo from '@components/press/pitch-deck/logos/linkedin_logo.jpeg';
import geLogo from '@components/press/pitch-deck/logos/ge_digital_logo.jpeg';
import stitchFixLogo from '@components/press/pitch-deck/logos/stitch_fix_logo.jpeg';
import pinterestLogo from '@components/press/pitch-deck/logos/pinterest_logo.jpeg';
import ucBerkeleyLogo from '@components/press/pitch-deck/logos/uc_berkeley_logo.jpeg';
import claudeLogo from '@components/press/pitch-deck/logos/claude-color.png';
import chatgptLogo from '@components/press/pitch-deck/logos/ChatGPT-Logo.png';
import geminiLogo from '@components/press/pitch-deck/logos/gemini_logo.png';

/*
 * June, 2026-10-09: "tidy this up - it doesn't do any favors, bring in the
 * old team slide, then augment it with the Built so far and hiring", and
 * "hiring is AI Applications Engineer, AI Growth Engineer, Community
 * Manager, and June". The old slide (`slides/team-slide.tsx`, which the
 * April deck carries as sent) sizes itself to the window; this is its
 * founder and AI team, in its own words and logos, on the 1920x1080 canvas
 * the rest of this deck stands on.
 */

const JUNE_PHOTO_URL = 'https://latlonglabs.com/img/june_bio_pic.png';

const COMPANIES: ReadonlyArray<{ src: string; alt: string }> = [
  { src: linkedinLogo, alt: 'LinkedIn' },
  { src: geLogo, alt: 'GE Digital' },
  { src: stitchFixLogo, alt: 'Stitch Fix' },
  { src: pinterestLogo, alt: 'Pinterest' },
  { src: ucBerkeleyLogo, alt: 'UC Berkeley' },
];

const BUILT: readonly string[] = [
  'Zero to App Store in six months',
  'Four products live on iOS and Android',
  '30+ user studies with moms',
  'About $300 a month in tokens and infrastructure',
];

/** The team this round makes: June and the three she hires. */
const TEAM: ReadonlyArray<{ initials: string; role: string; focus?: boolean }> = [
  { initials: 'JA', role: 'June · CEO', focus: true },
  { initials: 'AE', role: 'AI Applications Engineer' },
  { initials: 'GE', role: 'AI Growth Engineer' },
  { initials: 'CM', role: 'Community Manager' },
];

export const DTeamSlide: React.FC = () => (
  <StatementSlide
    section="Team"
    question="Why is this the team to win?"
    headline="A founder who is the customer, and who has built consumer ML and growth at scale."
  >
    <TeamBody />
  </StatementSlide>
);

const TeamBody: React.FC = () => {
  const arrive = useArrive();
  return (
    <Stack>
      <Cards>
        <Card {...arrive(1)}>
          <Photo style={{ backgroundImage: `url(${JUNE_PHOTO_URL})` }} role="img" aria-label="Dr. June Andrews" />
          <div>
            <Name>Dr. June Andrews</Name>
            <Role>Founder</Role>
            <Bio>
              <li>Building and leading teams for cutting-edge ML products with worldwide impact</li>
              <li>First real-time ML for GE’s jet engine fleet, 60% of the world’s commercial jet engines</li>
              <li>First platform predicting fashion’s performance before production, at Stitch Fix</li>
              <li>Data for growth at LinkedIn, 2013–2015: sign-ups, MAU and engagement</li>
              <li>Search for Pinterest and Yelp; UC Berkeley lecturer in applied ML</li>
              <li>Mom of two in a dual-career household: the beachhead herself</li>
            </Bio>
            <Logos>
              {COMPANIES.map((company) => (
                <img key={company.alt} src={company.src} alt={company.alt} />
              ))}
            </Logos>
            <Chips>
              <Chip>PhD Cornell · Applied Math (Social Networks)</Chip>
              <Chip>NSF Fellow</Chip>
            </Chips>
          </div>
        </Card>
        <Card {...arrive(2)}>
          <AiMark aria-hidden="true">
            <img src={claudeLogo} alt="" style={{ left: 38, top: 10 }} />
            <img src={geminiLogo} alt="" style={{ left: 10, top: 62 }} />
            <img src={chatgptLogo} alt="" style={{ left: 66, top: 62 }} />
          </AiMark>
          <div>
            <Name>AI Engineering Team</Name>
            <Role>CTO: Claude, Gemini and Codex</Role>
            <Prose>
              They build everything in house on Google Cloud: code, research, data mining, branding and marketing.
              Cheaper, more customized and better integrated than a stack of vendors.
            </Prose>
            <Chips>
              <Chip>AI-native from day one</Chip>
            </Chips>
          </div>
        </Card>
      </Cards>
      <Lower>
        <Panel {...arrive(3)}>
          <PanelLabel>Built so far</PanelLabel>
          <BuiltList>
            {BUILT.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </BuiltList>
        </Panel>
        <Panel {...arrive(4)}>
          <PanelLabel>The team this round makes</PanelLabel>
          <Roster>
            {TEAM.map((member) => (
              <Member key={member.role}>
                <Avatar $focus={member.focus ?? false}>{member.initials}</Avatar>
                <span>{member.role}</span>
              </Member>
            ))}
          </Roster>
        </Panel>
      </Lower>
    </Stack>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: 26px;
`;

const Card = styled(motion.div)`
  display: grid;
  grid-template-columns: 132px 1fr;
  column-gap: 26px;
  padding: 28px 30px;
  border-radius: 24px;
  background: ${colors.white};
  box-shadow: 0 14px 28px ${colors.shadowSoft};
`;

const Photo = styled.div`
  width: 132px;
  height: 132px;
  border-radius: 50%;
  border: 3px solid ${colors.brand.primary};
  background-position: center 20%;
  background-size: 140%;
  background-color: ${colors.brand.primary};
`;

/* The old slide's mark: Claude, Gemini and ChatGPT in a triangle on deep
   slate and seafoam. */
const AiMark = styled.div`
  position: relative;
  width: 132px;
  height: 132px;
  border-radius: 50%;
  border: 3px solid ${colors.brand.primary};
  background: linear-gradient(135deg, ${colors.highlight.extraDark}, ${colors.brand.exxtraDark});

  img {
    position: absolute;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.white};
    object-fit: contain;
    padding: 6px;
  }
`;

const Name = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 28px;
  font-weight: 600;
  color: ${colors.neutral.extraDark};
`;

const Role = styled.div`
  margin-top: 2px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 19px;
  color: ${colors.brand.exxtraDark};
`;

const Bio = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;

  li {
    margin-bottom: 4px;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 17px;
    line-height: 1.35;
    color: ${colors.neutral.darkMedium};
  }
`;

const Prose = styled.p`
  margin: 12px 0 0;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 18px;
  line-height: 1.45;
  color: ${colors.neutral.darkMedium};
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  margin-top: 14px;

  img {
    height: 26px;
    opacity: 0.85;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Chip = styled.span`
  padding: 4px 12px;
  border-radius: 9999px;
  border: 1px solid ${colors.brand.primaryDark};
  background: ${colors.brand.primaryLight};
  font-family: ${typography.fontFamily.small};
  font-size: 14px;
  color: ${colors.highlight.dark};
`;

const Lower = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 26px;
`;

const Panel = styled(motion.div)`
  padding: 20px 28px;
  border-radius: 20px;
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
`;

const PanelLabel = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;

const BuiltList = styled.ul`
  margin: 10px 0 0;
  padding-left: 20px;
  columns: 2;
  column-gap: 30px;

  li {
    margin-bottom: 4px;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 17px;
    line-height: 1.35;
    color: ${colors.neutral.extraDark};
    break-inside: avoid;
  }
`;

const Roster = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
`;

const Member = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  line-height: 1.3;
  color: ${colors.neutral.extraDark};
`;

const Avatar = styled.div<{ $focus: boolean }>`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${colors.white};
  box-shadow: 0 2px 6px ${colors.shadowSoft};
  background: ${(p) => (p.$focus ? colors.accent.coral.exxxtraDark : colors.brand.primaryDark)};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => (p.$focus ? colors.white : colors.highlight.dark)};
`;
